import { Link, useLocation } from 'react-router-dom';
import { useMemo } from 'react';
import { useDistrict } from '../features/districts-admin';

/**
 * Sprint BG-Menu (第 3 段): パンくずナビ component。
 *
 * pathname を分割してラベル化して main 上部に表示する。
 *   - /                                    → ホーム
 *   - /districts                           → ホーム > 全地区
 *   - /districts/:id                       → ホーム > 全地区 > <地区名>
 *   - /districts/:id/moderation            → ホーム > 全地区 > <地区名> > 投稿モデレーション
 *   - /masters/ai-analysis-profile         → ホーム > テンプレ・マスタ > AI 解析 統合エディタ
 *   - /users                               → ホーム > 権限・監査 > 管理者アカウント
 *   - /operation-logs                      → ホーム > 権限・監査 > 操作ログ
 *   - /help                                → ホーム > 使い方ガイド
 *
 * 地区名は `useDistrict(id)` で解決 (取得前は id を表示)。
 */

interface Crumb {
  label: string;
  to?: string;
}

/** /districts/:id/... の id を抽出。完全に pure。 */
export function extractDistrictIdForBreadcrumb(pathname: string): string | null {
  const m = /^\/districts\/([^/]+)(?:\/.*)?$/.exec(pathname);
  if (!m) return null;
  const id = m[1];
  if (!id || id === 'new') return null;
  return id;
}

const MASTER_LABELS: Record<string, string> = {
  'ai-analysis-profile': 'AI 解析 統合エディタ',
  'ai-category': 'AI カテゴリ',
  'ai-output-format': 'AI 出力フォーマット',
  'street-survey-templates': '机上調査テンプレート',
  'ai-dialogue-settings': 'AI 対話設定',
  'analysis-display': '分析表示設定',
  'score-dimension': 'スコア軸定義',
  'card-print': 'カード印刷テンプレ',
  'survey-preset': 'アンケートテンプレ',
  'verification-template': '検証テンプレ',
  'mode-profiles': 'モードプロファイル',
};

const DISTRICT_SUB_LABELS: Record<string, string> = {
  'data-settings': '入力項目',
  moderation: '投稿モデレーション',
  'insights-moderation': '気づき・処方箋のモデレーション',
  announcements: 'お知らせ',
  sessions: 'セッション',
  participants: 'ユーザー管理',
  surveys: '運用中のアンケート',
  verification: '運用中の検証',
  'verification-results': '検証結果',
  export: 'エクスポート',
  import: 'インポート',
  'photo-import': '写真まとめて一括投稿',
  cards: 'カード印刷 (PDF)',
  gis: 'GISデータ',
  display: '地図・表示の設定',
  ai: 'AI 設定',
  'ai-editor': 'AI 解析 統合エディタ (地区)',
  misc: 'その他設定',
  distribute: '配分セット生成',
  templates: 'テンプレート',
  assignmentSets: '配分セット',
  plans: '配分の予約',
  responses: '回答一覧',
  new: '新規作成',
  edit: '編集',
};

/** pathname を pure に crumbs[] に分解 (地区名は別途解決して差し込む)。 */
export function buildCrumbs(pathname: string, districtName: string | null): Crumb[] {
  const parts = pathname.split('/').filter(Boolean);
  const crumbs: Crumb[] = [{ label: 'ホーム', to: '/' }];

  if (parts.length === 0) return crumbs;

  // /districts/*
  if (parts[0] === 'districts') {
    crumbs.push({ label: '全地区', to: '/districts' });
    if (parts.length >= 2) {
      const id = parts[1] ?? '';
      crumbs.push({
        label: districtName ?? id,
        to: `/districts/${id}`,
      });
      // /districts/:id/<sub>/...
      for (let i = 2; i < parts.length; i++) {
        const seg = parts[i] ?? '';
        const label = DISTRICT_SUB_LABELS[seg] ?? seg;
        crumbs.push({ label });
      }
    }
    return crumbs;
  }

  // /masters/*
  if (parts[0] === 'masters') {
    crumbs.push({ label: 'テンプレ・マスタ' });
    if (parts.length >= 2) {
      const seg = parts[1] ?? '';
      crumbs.push({ label: MASTER_LABELS[seg] ?? seg });
    }
    return crumbs;
  }

  // single-segment well-known
  if (parts[0] === 'users') {
    crumbs.push({ label: '権限・監査' });
    crumbs.push({ label: '管理者アカウント' });
    return crumbs;
  }
  if (parts[0] === 'operation-logs') {
    crumbs.push({ label: '権限・監査' });
    crumbs.push({ label: '操作ログ' });
    return crumbs;
  }
  if (parts[0] === 'help') {
    crumbs.push({ label: '使い方ガイド' });
    return crumbs;
  }

  // fallback
  parts.forEach((p) => crumbs.push({ label: p }));
  return crumbs;
}

export function Breadcrumbs() {
  const { pathname } = useLocation();
  const districtId = extractDistrictIdForBreadcrumb(pathname);
  const districtState = useDistrict(districtId);
  const districtName = districtState.status === 'ready' ? districtState.district.name : null;

  const crumbs = useMemo(() => buildCrumbs(pathname, districtName), [pathname, districtName]);

  if (crumbs.length <= 1) return null;

  return (
    <nav aria-label="パンくずリスト" className="mb-4 text-xs text-slate-500">
      <ol className="flex flex-wrap items-center gap-1">
        {crumbs.map((c, idx) => {
          const isLast = idx === crumbs.length - 1;
          return (
            <li key={`${c.label}-${idx}`} className="flex items-center gap-1">
              {idx > 0 && <span className="text-slate-300">/</span>}
              {c.to && !isLast ? (
                <Link to={c.to} className="hover:text-primary-700 hover:underline">
                  {c.label}
                </Link>
              ) : (
                <span
                  aria-current={isLast ? 'page' : undefined}
                  className={isLast ? 'text-slate-700 font-medium' : ''}
                >
                  {c.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
